import type { Message, Paginator } from "@twilio/conversations";
import { messagesMap } from "../../conversations-objects";

const delayMs = Number(process.env.REACT_APP_MOCK_TWILIO_DELAY_MS ?? "150");
const pageSizeDefault = Number(process.env.REACT_APP_MOCK_PAGE_SIZE ?? "30");

// facoltativo: per far vedere outbound a destra se confronti con operator.email
const MOCK_AUTHOR = String(
  process.env.REACT_APP_MOCK_OPERATOR_EMAIL ?? "operator@example.com"
);

type MockDb = Record<string, Message[]>;
const DB: MockDb = {};

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function makeMockMessage(params: {
  sid: string;
  index: number;
  author: string;
  body: string;
  dateCreated: Date;
}): Message {
  const msg: Partial<Message> = {
    sid: params.sid,
    index: params.index,
    author: params.author,
    body: params.body,
    dateCreated: params.dateCreated,
    attributes: {},
    participantSid: null as any,
    attachedMedia: [] as any,
    aggregatedDeliveryReceipt: null as any,

    updateBody: async (text: string) => {
      (msg as any).body = text;
      return msg as Message;
    },
    remove: async (): Promise<Message> => {
      return {} as Message;
    },
    updateAttributes: async (attrs: any) => {
      (msg as any).attributes = attrs;
      return msg as Message;
    },
  };

  messagesMap.set(params.sid, msg as Message);

  return msg as Message;
}

function seedIfNeeded(convoSid: string) {
  if (DB[convoSid]?.length) return;

  const now = Date.now();
  const seed: Message[] = [
    makeMockMessage({
      sid: `IM-${convoSid}-1`,
      index: 1,
      author: "+393331110000",
      body: "Ciao! Questo è un messaggio inbound (mock).",
      dateCreated: new Date(now - 1000 * 60 * 60),
    }),
    makeMockMessage({
      sid: `IM-${convoSid}-2`,
      index: 2,
      author: MOCK_AUTHOR,
      body: "Ciao! Ricevuto, come posso aiutarti?",
      dateCreated: new Date(now - 1000 * 60 * 55),
    }),
    makeMockMessage({
      sid: `IM-${convoSid}-3`,
      index: 3,
      author: "+393331110000",
      body: "Ho un problema con il mio ordine.",
      dateCreated: new Date(now - 1000 * 60 * 50),
    }),
    makeMockMessage({
      sid: `IM-${convoSid}-4`,
      index: 4,
      author: MOCK_AUTHOR,
      body: "Ok, mi dai il numero d’ordine?",
      dateCreated: new Date(now - 1000 * 60 * 48),
    }),
  ];

  DB[convoSid] = seed;
}

function makePaginator(
  all: Message[],
  start: number,
  pageSize: number
): Paginator<Message> {
  const safeStart = Math.max(0, Math.min(start, all.length));
  const items = all.slice(safeStart, safeStart + pageSize);
  const hasPrevPage = safeStart > 0;

  const paginator: Partial<Paginator<Message>> = {
    items,
    hasPrevPage,
    prevPage: async () => {
      await sleep(delayMs);
      const nextStart = Math.max(0, safeStart - pageSize);
      return makePaginator(all, nextStart, pageSize);
    },
  };

  return paginator as Paginator<Message>;
}

/**
 * Ritorna l'ultima "pagina" (più recente) e permette prevPage() verso i messaggi più vecchi.
 */
export async function mockGetMessagesByConversationSid(
  convoSid: string,
  pageSize: number = pageSizeDefault
): Promise<Paginator<Message>> {
  seedIfNeeded(convoSid);
  await sleep(delayMs);

  const all = DB[convoSid] ?? [];
  const start = Math.max(0, all.length - pageSize); // ultima pagina
  return makePaginator(all, start, pageSize);
}
